<template>
  <!-- For Travelski Express: show only the terms link, no badge -->
  <div v-if="isTravelskiExpress">
    <TermsAndConditionsLink 
      :linkText="t('payments.termsAndConditions')"
      :custom-url="TRAVELSKI_EXPRESS_TERMS_URL"
    />
  </div>
  <!-- For regular bookings: show full payment condition with badge -->
  <BasePaymentCondition
    v-else
    border-class="blue-border"
    text-class="text-primary-white"
    background-class="bg-primary-blue"
  >
    <template #payment-condition-icon>
      <FlexiblePaymentIcon />
    </template>
    <template #payment-condition-type>
      {{ t('payments.flexProduct') }}
    </template>
    <template #payment-condition__title__ref>
      {{ t('payments.flexTitle') }}
    </template>
    <template #payment-condition-description>
      {{ descriptionText }}
      <TermsAndConditionsLink 
        :linkText="t('payments.termsAndConditions')"
      />
    </template>
  </BasePaymentCondition>
</template>

<script lang="ts" setup>
  // Vue
  import { computed, defineProps } from 'vue'

  // Components
  import BasePaymentCondition from './BasePaymentCondition.vue'
  import TermsAndConditionsLink from '@/components/common/TermsAndConditionsLink.vue'

  // Translations
  import { useI18n } from 'vue-i18n'
  const { t, d } = useI18n()

  // Icons
  import FlexiblePaymentIcon from '@/assets/FlexiblePaymentIcon.vue'

  // Config
  import { isTravelskiExpress as checkIsTravelskiExpress } from '@/config'
  import { TRAVELSKI_EXPRESS_TERMS_URL } from '@/config/termsAndConditions'

  // Props
  interface Props {
    cancellationUntil: string | null
    prestId: number
    liheId: number
    liheName?: string
    packageCode?: string
  }

  const props = defineProps<Props>()

  // Check if this is a Travelski Express booking
  const isTravelskiExpress = computed(() => {
    return checkIsTravelskiExpress(props.liheId, props.liheName, props.packageCode)
  })

  // Computed description text
  const descriptionText = computed(() => {
    if (props.cancellationUntil) {
      return t('payments.flexDescription', {
        cancellationUntil: d(props.cancellationUntil),
      })
    }
    // Fallback when no cancellation date - show description without date
    const fullDescription = t('payments.flexDescription', { cancellationUntil: '' })
    // Remove "until [date]" part from translations that require it
    return fullDescription
      .replace(/\s*until\s*/i, '')
      .replace(/\s*jusqu'?au\s*/i, '')
      .replace(/\s*tot\s*/i, '')
      .trim()
  })
</script>

<style scoped></style>
